import React, { ComponentProps, useState } from 'react';
import {
  CheckBoxParam,
  Form,
  Input,
  Radio,
  RadioGroup,
  RadioParam,
  Row,
  SonForm,
  SubmitButton,
  Switch,
  TextParam,
  useForm,
} from '../../../Form2';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { asFetchableView } from '../../../ViewFetcher';
import FormsRow, { FormsRowModifier } from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import { FieldsetModifier } from '../../../../_lib/Form2/elements/Fieldset/Fieldset';
import { useRefreshBlock } from '../../../BlockFetcher';

export type ExternalContentForm = SonForm<
  | TextParam<'title'>
  | TextParam<'url'>
  | TextParam<'caption'>
  | RadioParam<'orientation', 'portrait' | 'landscape'>
  | CheckBoxParam<'popUp'>
  | TextParam<'popUpUrl'>
  | RadioParam<'size', 'small' | 'medium' | 'large'>
>;

type ExternalContentEditProps = {
  form: ExternalContentForm;
};

function ExternalContentEdit({ form }: ExternalContentEditProps) {
  const { t } = useSonTranslation('ExternalContent');

  const { delayedRefresh } = useRefreshBlock();

  const { formProps, params, fetchState } = useForm({
    form,
    onSuccess: delayedRefresh,
  });

  const titleParam = params.findByName('title');
  const urlParam = params.findByName('url');
  const captionParam = params.findByName('caption');
  const sizeCombinationParam = params.getAllByName('size');
  const orientationCombinationParam = params.getAllByName('orientation');
  const popUpParam = params.findByName('popUp');
  const overlayUrlParam = params.findByName('popUpUrl');

  const [popUpEnabled, setPopUpEnabled] = useState(popUpParam ? popUpParam?.checked : false);

  const getAddressesForHelpText = (domainAllowlist: string[]): string => {
    return domainAllowlist.join(', ');
  };

  const onInvalidUrl = (event: React.FormEvent<HTMLInputElement>, type: 'url' | 'popUpUrl') => {
    if (event.currentTarget.validity.patternMismatch) {
      event.currentTarget.setCustomValidity(
        t(`input.${type}.patternMismatch`, {
          addresses: getAddressesForHelpText(urlParam?.constraints?.domainAllowlist || []),
        })
      );
    }
  };

  return (
    <Form id="external-content-form" {...formProps}>
      {titleParam && (
        <Row>
          <Input label={t('input.title.label')} helpText={t('input.title.helpText')} param={titleParam} />
        </Row>
      )}
      {urlParam && (
        <Row>
          <Input
            label={t('input.url.label')}
            helpText={t('input.url.helpText')}
            param={urlParam}
            onInvalid={event => onInvalidUrl(event, 'url')}
          />
        </Row>
      )}
      {captionParam && (
        <Row>
          <Input label={t('input.caption.label')} param={captionParam} />
        </Row>
      )}
      {sizeCombinationParam.length > 1 && (
        <RadioGroup
          fieldsetModifier={[FieldsetModifier.LIKE_LABEL, FieldsetModifier.INLINE_RADIOS]}
          legend={t('input.size.fieldSetLabel')}
          required={orientationCombinationParam.some(r => r.required)}
        >
          {sizeCombinationParam.reverse().map(radio => (
            <Radio key={radio.value} label={t(`input.size.label.${radio.value}`)} param={radio} />
          ))}
        </RadioGroup>
      )}
      {orientationCombinationParam.length > 1 && (
        <RadioGroup
          fieldsetModifier={[FieldsetModifier.LIKE_LABEL, FieldsetModifier.INLINE_RADIOS]}
          legend={t('input.orientation.fieldSetLabel')}
          required={orientationCombinationParam.some(r => r.required)}
        >
          {orientationCombinationParam.map(radio => (
            <Radio key={radio.value} label={t(`input.orientation.label.${radio.value}`)} param={radio} />
          ))}
        </RadioGroup>
      )}
      {popUpParam && (
        <FormsRow modifier={FormsRowModifier.RIGHT}>
          <Switch param={popUpParam} label={t('input.popUp.label')} onClick={() => setPopUpEnabled(!popUpEnabled)} />
        </FormsRow>
      )}
      {overlayUrlParam && popUpEnabled && (
        <Row>
          <Input
            label={t('input.popUpUrl.label')}
            helpText={t('input.popUpUrl.helpText')}
            param={overlayUrlParam}
            onInvalid={event => onInvalidUrl(event, 'popUpUrl')}
          />
        </Row>
      )}
      <SubmitButton
        buttonLabel={t('submit')}
        messageSuccess={t('status.success')}
        messageSending={t('status.sending')}
        messageFail={t('status.fail')}
        fetchState={fetchState}
      />
    </Form>
  );
}

export const ExternalContentEditView = asFetchableView(ExternalContentEdit);
export type ExternalContentEditView = ComponentProps<typeof ExternalContentEditView>;
